/**
 * The story document (spec 023): a story's graph by scene key plus every
 * scene's 021 document, built from what the API returns and turned back into
 * the persisted `StoryGraph`. Both directions are pure.
 */
import type {
  PictureMap, StoredPictureMap, StoryApiResponse, StoryGraph, StoryGraphNode, StorySceneWithScene,
} from '../@types';
import { isPictureKind, storedSlotOf } from '../pictures/pictureMap';
import { validateGraph, type GraphScene, type GraphValidationResult } from '../utils/validateGraph';
import { createDefaultBackground, stripRetiredSceneFields } from '../utils/sceneContentDefaults';
import type { SceneDocument } from '../operations/types';
import type { StoryDocScene, StoryDocument } from './types';

/** The key of an existing story scene. */
export const storySceneKey = (storySceneId: number): string => `ss-${storySceneId}`;

/** The `storySceneId` an `ss-<n>` key names, or undefined for any other key. */
export function storySceneIdOfKey(key: string): number | undefined {
  const m = /^ss-(\d+)$/.exec(key);
  return m ? Number(m[1]) : undefined;
}

/** What the story scene list and its loaded scenes give the document. */
export type StorySource = Pick<StoryApiResponse, 'name' | 'graph'> & {
  storyScenes: Pick<StorySceneWithScene, 'id' | 'mode' | 'sceneName' | 'choices'>[];
  /** The story's picture map, derived (`uses` are dropped) or stored; absent is `{slots: []}` (spec 026). */
  pictureMap?: PictureMap | StoredPictureMap | null;
};

/** A picture map as stored: every slot without `uses` (spec 026); a slot of an unknown kind is dropped. Absent or malformed is `{slots: []}`. */
export function storedPictureMapFrom(map: PictureMap | StoredPictureMap | null | undefined): StoredPictureMap {
  const slots = Array.isArray(map?.slots) ? map!.slots : [];
  return {
    slots: slots
      .filter((s) => s && typeof s.id === 'string' && typeof s.subject === 'string' && isPictureKind(s.kind))
      .map((s) => storedSlotOf({ ...s, variant: s.variant ?? '', brief: s.brief ?? '', why: s.why ?? '' })),
  };
}

/** The document's stored picture map, for persistence (spec 026). */
export const storedPictureMapOf = (doc: Pick<StoryDocument, 'pictureMap'>): StoredPictureMap =>
  storedPictureMapFrom(doc.pictureMap);

/**
 * The fallback document of a story scene whose content was not supplied: its
 * name and choices from the scene list, nothing else.
 */
function listedSceneDocument(ss: StorySource['storyScenes'][number]): SceneDocument {
  return {
    name: ss.sceneName ?? '',
    background: createDefaultBackground(),
    sprites: [],
    dialogs: [],
    sounds: [],
    signals: [],
    ...(ss.choices ? { choices: ss.choices } : {}),
  } as unknown as SceneDocument;
}

/**
 * The document of a story. `scenes` maps a `storySceneId` to that scene's
 * name and document; a story scene missing from it gets a content-less
 * document from the scene list. A node whose scene is not in the story keeps
 * its `ss-<id>` key (the summary marks it unknown). `end` nodes, which no
 * editor creates, are not carried. A scene's `keyframes` are carried as
 * supplied (021: part of the document); what Storyboard left on a scene
 * (`stripRetiredSceneFields`, spec 006) is not.
 */
export function storyDocumentFrom(
  story: StorySource,
  scenes: ReadonlyMap<number, { name: string; doc: SceneDocument }>,
): StoryDocument {
  const graph = story.graph ?? { nodes: [], edges: [], startNodeId: '' };
  const docScenes: StoryDocScene[] = story.storyScenes.map((ss) => {
    const loaded = scenes.get(ss.id);
    const doc = loaded ? stripRetiredSceneFields(loaded.doc) : listedSceneDocument(ss);
    return {
      key: storySceneKey(ss.id),
      storySceneId: ss.id,
      mode: ss.mode,
      name: loaded?.name ?? doc.name ?? ss.sceneName ?? '',
      doc,
    };
  });
  return {
    name: story.name,
    startNodeId: graph.startNodeId ?? '',
    nodes: graph.nodes
      .filter((n) => n.type !== 'end')
      .map((n) => ({ id: n.id, sceneKey: storySceneKey(n.storySceneId), position: { x: n.position.x, y: n.position.y } })),
    edges: graph.edges.map((e) => ({ ...e })),
    scenes: docScenes,
    pictureMap: storedPictureMapFrom(story.pictureMap),
  };
}

/**
 * The persisted graph of a document. `keyToStorySceneId` maps the minted
 * keys of created scenes (and may override `ss-` keys); an `ss-<n>` key not
 * in it is `n`. A key that resolves to nothing becomes 0 (the validator then
 * reports the node's scene as unknown).
 */
export function storyGraphFrom(
  doc: Pick<StoryDocument, 'nodes' | 'edges' | 'startNodeId'>,
  keyToStorySceneId: ReadonlyMap<string, number> = new Map(),
): StoryGraph {
  const nodes: StoryGraphNode[] = doc.nodes.map((n) => ({
    id: n.id,
    storySceneId: keyToStorySceneId.get(n.sceneKey) ?? storySceneIdOfKey(n.sceneKey) ?? 0,
    position: { x: n.position.x, y: n.position.y },
    type: 'scene',
  }));
  return { nodes, edges: doc.edges.map((e) => ({ ...e })), startNodeId: doc.startNodeId };
}

/** The scene with this key, or undefined. */
export const sceneByKey = (doc: Pick<StoryDocument, 'scenes'>, key: string): StoryDocScene | undefined =>
  doc.scenes.find((s) => s.key === key);

/**
 * `validateGraph` (spec 010) over the document, created scenes included:
 * every scene key gets a stand-in `storySceneId`, so the validator's words
 * (which name nodes, edges and scene names) are the same as for the stored
 * graph.
 */
export function validateStoryDocument(doc: StoryDocument): GraphValidationResult {
  const ids = new Map<string, number>();
  const byId: Record<number, GraphScene> = {};
  doc.scenes.forEach((s, i) => {
    ids.set(s.key, i + 1);
    byId[i + 1] = { name: s.doc.name || s.name, choices: s.doc.choices ?? [] };
  });
  // Unknown keys resolve to an id no scene has.
  const unknown = new Map(doc.nodes.filter((n) => !ids.has(n.sceneKey)).map((n) => [n.sceneKey, -1]));
  return validateGraph(storyGraphFrom(doc, new Map([...ids, ...unknown])), byId);
}
